import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../../components/Buttons/button";
import Search from "../../../../../assets/images/search.png";
import {
  getServiceMembers,
  setServiceMemberCreatedFalse,
  selectCompanyDashboard,
  setServiceMemberUpdatedFalse,
} from "../../../../../redux/slices/companyDashboardSlice";
import { getInitials } from "../../../../../shared/lib/getInitials";
import { getColorHex } from "../../../../../shared/lib/getColorHex";
import BasicModal from "../../../../models/models";
import Unicon from "../../../../../assets/images/arrow-down-circle.svg";
import cn from "classnames";
import MenuDropdown from "../../../../Dropdown/menu-dropdown"; // Import reusable dropdown
import menuDot from "../../../../../assets/images/menuDot.png";

// Helper function to parse date strings (DD/MM/YYYY)
const parseDate = (dateString) => {
  const [day, month, year] = dateString.split("/");
  return new Date(`${year}-${month}-${day}`);
};

// Sub-component for table header with sorting
const TableHeader = ({ title, onSort, sortOrder, widthClass = "", plClass = "pl-0" }) => (
  <th className={`text-left text-secondary border-[#434A5D] py-[12px] ${plClass} pr-[16px] ${widthClass}`}>
    <Button
      className="sorting_btn_wrapper"
      secondaryHoverOutlined={true}
      outlined={true}
      onClick={onSort}
    >
      <span className="flex items-center text-[#A5AEBC]">
        {title}
        <img
          className={cn("sortingicon", { "rotate-180": sortOrder === "desc" })}
          src={Unicon}
          alt="icon"
        />
      </span>
    </Button>
  </th>
);

const Members = ({ id }) => {
  const dispatch = useDispatch();
  const companyDashboardState = useSelector(selectCompanyDashboard);
  const { serviceMemberData, serviceMemberCreated, serviceMemberUpdated } = companyDashboardState;
  const serviceMembers = serviceMemberData.service_members;

  const [popup, setPopup] = useState(false);
  const [state, setState] = useState(0);
  const [userId, setUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortedUsers, setSortedUsers] = useState([]);
  const [sortOrderName, setSortOrderName] = useState("asc");
  const [sortOrderType, setSortOrderType] = useState("asc");
  const [sortOrderAdded, setSortOrderAdded] = useState("asc");
  const [sortOrderLastActive, setSortOrderLastActive] = useState("asc");
  const [userType, setUserType] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);

  useEffect(() => {
    dispatch(getServiceMembers(id));
  }, [id, dispatch, serviceMemberCreated, serviceMemberUpdated]);

  useEffect(() => {
    if (serviceMembers) {
      setSortedUsers([...serviceMembers]);
    }
  }, [serviceMembers]);

  const handleMenuClick = (userIdParam, userTypeParam) => {
    setUserId(userIdParam);
    setUserType(userTypeParam);
  };

  const handleChangeMemberType = () => {
    setPopup(true);
    setState(42);
    dispatch(setServiceMemberUpdatedFalse());
  };

  const handleRemoveMember = () => {
    setPopup(true);
    setState(39);
    dispatch(setServiceMemberUpdatedFalse());
  };

  const handleAddServiceMember = () => {
    setPopup(true);
    setState(40);
  };

  const handleMemberType = () => {
    setPopup(true);
    setState(41);
  };

  // Toggle dropdown for specific index
  const toggleDropdown = (index) => {
    setDropdownOpen((prevOpenDropdown) => prevOpenDropdown === index ? null : index);
  };

  const handleMember = () => {
    const sorted = [...serviceMembers].sort((a, b) => {
      if (sortOrderName === "asc") {
        return a.name.localeCompare(b.name);
      } else {
        return b.name.localeCompare(a.name);
      }
    });
    setSortOrderName(sortOrderName === "asc" ? "desc" : "asc");
    setSortedUsers(sorted);
  };

  const handleType = () => {
    const sorted = [...serviceMembers].sort((a, b) => {
      const typeA = a.type || "";
      const typeB = b.type || "";
      if (sortOrderType === "asc") {
        return typeA.localeCompare(typeB);
      } else {
        return typeB.localeCompare(typeA);
      }
    });
    setSortOrderType(sortOrderType === "asc" ? "desc" : "asc");
    setSortedUsers(sorted);
  };

  const handleAdded = () => {
    const sorted = [...serviceMembers].sort((a, b) => {
      const dateA = parseDate(a.added);
      const dateB = parseDate(b.added);
      if (sortOrderAdded === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
    setSortOrderAdded(sortOrderAdded === "asc" ? "desc" : "asc");
    setSortedUsers(sorted);
  };

  const handleLastActive = () => {
    const sorted = [...serviceMembers].sort((a, b) => {
      const dateA = parseDate(a.last_active);
      const dateB = parseDate(b.last_active);
      if (sortOrderLastActive === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
    setSortOrderLastActive(sortOrderLastActive === "asc" ? "desc" : "asc");
    setSortedUsers(sorted);
  };

  const displayMembers = sortedUsers.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Generate menu items for each member
  const getMenuItems = (user, index) => [
    {
      label: (
        <>
          <p className="text-sm font-medium text-white">Change member type</p>
          <span className="!text-[12px] font-normal text-secondary">
            Updates will take effect immediately
          </span>
        </>
      ),
      onClick: () => {
        toggleDropdown(index);
        handleChangeMemberType();
      },
    },
    {
      label: (
        <>
          <p className="text-sm font-medium text-red-400">Remove member</p>
          <span className="!text-[12px] font-normal text-secondary">
            Employee will no longer have access to sign in to this service’s dashboard
          </span>
        </>
      ),
      onClick: () => {
        toggleDropdown(index);
        handleRemoveMember();
      },
    },
  ];

  return (
    <div className="manage-service-wrapper mt-0 md:mt-2 ManageServicesBox">
      <div className="flex flex-col md:flex-row md:justify-between gap-[10px] md:gap-[24px] md:items-start">
        <div className="flex flex-row justify-start gap-[24px] items-center">
          <div className="flex flex-col">
            <span className="text-primary !text-[24px] !leading-[36px] text-left">
              Service members
            </span>
            <span className="text-secondary !text-[14px] !leading-[21px] text-left">
              <span>
                Service members have access to this service’s dashboard and more
                depending on their{" "}
              </span>
              <span>
                <a
                  href="#"
                  className="memberLink"
                  onClick={() => handleMemberType()}
                >
                  member type.
                </a>
              </span>
            </span>
          </div>
        </div>

        <Button
          outlined={true}
          hoverColor={true}
          secondary={true}
          secondaryHoverOutlined={true}
          onClick={() => {
            handleAddServiceMember();
            dispatch(setServiceMemberCreatedFalse());
          }}
        >
          + Add Member
        </Button>
      </div>

      <div className="searchBox mt-2 md:mt-4">
        <img src={Search} alt="icon" className="searchIcon" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search member"
          className="searchInput !not-italic"
        />
      </div>

      <div className="rounded-t-xl !mt-0 pb-[20px] w-[100%] scrollbar-hide scrollable_data">
        <div className="overflow-x-auto scrollbar-hide custom_scroll_hide mobile-scroll">
          <table className="sticky top-0">
            <thead className="table-head sticky top-0 bg-[#363D50]">
              <tr>
                <TableHeader
                  title="Member"
                  onSort={handleMember}
                  sortOrder={sortOrderName}
                  widthClass="w-[350px]"
                />
                <TableHeader
                  title="Type"
                  onSort={handleType}
                  sortOrder={sortOrderType}
                />
                <TableHeader
                  title="Added"
                  onSort={handleAdded}
                  sortOrder={sortOrderAdded}
                />
                <TableHeader
                  title="Last active"
                  onSort={handleLastActive}
                  sortOrder={sortOrderLastActive}
                />
                <th className="text-left text-secondary border-[#434A5D] py-[12px] pl-0 pr-[16px]">
                  &nbsp;
                </th>
              </tr>
            </thead>

            <tbody>
              {displayMembers?.map((user, index) => {
                const [hexColor, hexWithOpacity] = getColorHex(user.id);
                return (
                  <tr key={user.id} className="cursor-pointer">
                    <td className="text-left text-secondary !text-[#FFFFFF] py-[16px] pl-2 pr-[16px]">
                      <div className="flex flex-row items-center gap-[10px]">
                        <div
                          style={{
                            backgroundColor: hexWithOpacity,
                            color: hexColor,
                          }}
                          className="w-[40px] h-[40px] rounded-full flex items-center justify-center"
                        >
                          {getInitials(user.name)}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-secondary lineHeight !text-[#FFFFFF] !text-left !font-medium">
                            {user.name}
                          </span>
                          <span className="text-secondary lineHeight !text-left">
                            {user.distinction}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="text-left text-secondary !text-[#FFFFFF] py-[16px] px-[16px] font_12px">
                      {user.type}
                    </td>
                    <td className="text-left text-secondary !text-[#FFFFFF] py-[16px] px-[16px] !font-medium font_12px">
                      {user.added}
                    </td>
                    <td className="text-left lineHeight text-secondary !text-[#FFFFFF] py-[16px] px-[16px] !font-medium">
                      {user.last_active}
                    </td>
                    <td className="text-left text-secondary !text-[#FFFFFF] py-[16px] px-[16px] !font-medium">
                      <MenuDropdown
                        isOpen={dropdownOpen === index}
                        toggleDropdown={() => {
                          handleMenuClick(user.id, user.type);
                          toggleDropdown(index);
                        }}
                        menuItems={getMenuItems(user, index)}
                        menuIconSrc={menuDot}
                        menuClassName="absolute z-20 right-[-16px] mt-2 py-2 w-[332px] bg-[#293043] rounded-3xl"
                        itemClassName="w-full text-left rounded-2xl px-4 py-2 border border-solid border-transparent transition-all duration-150 ease-linear hover:bg-[#363D50] active:!border-[#FFD74A] cursor-pointer"
                        dividerClassName="border-[#41485C] my-2 w-full"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {popup && (
        <BasicModal
          state={state}
          popup={popup}
          setPopup={setPopup}
          setState={setState}
          id={id}
          userId={userId}
          userType={userType}
        />
      )}
    </div>
  );
};

export default Members;
